import React from 'react';
import { Info, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CardFeesByType } from '@/types/savingsCalculator';

interface CardFeesByTypeCardProps {
  data: CardFeesByType;
  loading?: boolean;
}

const formatCurrency = (amount: number) =>
  amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const CardFeesByTypeCard: React.FC<CardFeesByTypeCardProps> = ({ data, loading = false }) => {
  const items = [
    {
      label: 'One-Time Purchases',
      amount: data.oneTimeFees,
      count: data.oneTimeCount,
    },
    {
      label: 'Subscriptions',
      amount: data.subscriptionFees,
      count: data.subscriptionCount,
    },
  ];

  return (
    <div className="border-2 border-muted rounded-xl bg-white dark:bg-background p-6">
      <div className="flex items-center gap-1.5 mb-4">
        <h3 className="text-base font-semibold text-foreground">Card Fees by Type</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>
                Breakdown of your Stripe card fees between one-time purchases and
                recurring subscription payments.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.label} className="space-y-1">
              <p className="text-sm text-muted-foreground">{item.label}</p>
              <p className="text-2xl font-bold text-red-500 dark:text-red-400">
                ${formatCurrency(item.amount)}
              </p>
              <p className="text-xs text-muted-foreground">
                from {item.count.toLocaleString()} card transaction{item.count !== 1 ? 's' : ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CardFeesByTypeCard;
